// FE-SCRM-033 — Closed Won → Account conversion UI.
//
// Two surfaces ship in one file: the header button on the
// OpportunityDetail page (``<ConvertToAccountControl>``) and the
// modal it opens (``<ConvertToAccountModal>``). The button gates on
// the opportunity's stage + converted state:
//
// * stage !== "closed_won" → hidden entirely.
// * stage === "closed_won" + converted_account_id IS NULL →
//   "Convert to Account" primary action.
// * stage === "closed_won" + converted_account_id set → "View
//   Account" Link that lands on ``/dashboard/accounts/{id}``.
//
// On Convert, the modal previews the handoff (producer, account
// lead), lets the producer override the account lead to a
// non-producer membership, and submits to BE-SCRM-032's endpoint.
// Success path: navigate to the new Account detail.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { Modal } from "@/design-system";
import { useTeamMembersQuery } from "@/features/sales/AddOpportunity/hooks";

import { CrmFormBanner, type FormBannerError } from "../components";

import { useConvertToAccountMutation } from "./hooks";
import type { OpportunityDetail } from "./types";


const INPUT_CLS = [
  "rounded-md border border-border-default bg-raised",
  "px-2 py-1.5 text-sm text-text-primary",
  "placeholder:text-text-tertiary outline-none",
  "transition-fast ease-out",
  "focus-visible:border-teal focus-visible:shadow-focus",
  "disabled:cursor-not-allowed disabled:opacity-60",
].join(" ");


const PRIMARY_BUTTON_CLS = [
  "rounded-md bg-teal px-3 py-1.5",
  "text-xs font-medium text-text-inverted",
  "hover:bg-teal-hover",
  "disabled:cursor-not-allowed disabled:opacity-60",
].join(" ");


const SECONDARY_BUTTON_CLS = [
  "rounded-md border border-border-default bg-raised px-3 py-1.5",
  "text-xs font-medium text-text-primary",
  "hover:bg-raised-hover",
  "disabled:cursor-not-allowed disabled:opacity-60",
].join(" ");


function humanizeServerError(code: string): string {
  const map: Record<string, string> = {
    opportunity_not_closed_won:
      "Move the opportunity to Closed Won before converting.",
    account_lead_not_in_entity:
      "The selected account owner isn't on this entity's team.",
    invalid_account_lead_membership_id:
      "Pick a valid team member for the account owner.",
  };
  return map[code] ?? code;
}


function deriveBannerErrors(serverError: unknown): FormBannerError[] {
  if (!serverError) return [];
  if (serverError instanceof ApiError) {
    const detail =
      (typeof serverError.body.detail === "string"
        ? serverError.body.detail
        : null) ?? serverError.message;
    return [{ message: humanizeServerError(detail) }];
  }
  if (serverError instanceof Error) {
    return [{ message: serverError.message }];
  }
  if (typeof serverError === "object" && serverError !== null) {
    const e = serverError as { body?: { detail?: unknown } };
    const detail = typeof e.body?.detail === "string"
      ? e.body.detail
      : "Conversion failed. Try again.";
    return [{ message: humanizeServerError(detail) }];
  }
  return [{ message: "Conversion failed. Try again." }];
}


export interface ConvertToAccountModalProps {
  open: boolean;
  onClose: () => void;
  opportunity: OpportunityDetail;
}


export function ConvertToAccountModal({
  open, onClose, opportunity,
}: ConvertToAccountModalProps): JSX.Element | null {
  const navigate = useNavigate();
  const teamMembers = useTeamMembersQuery();
  const mutation = useConvertToAccountMutation(opportunity.opportunity_id);

  // Account-lead override. Defaults to the opportunity's producer
  // (passed through to the BE so the modal stays explicit about what
  // it's about to submit even when the FE just echoes the default).
  const [leadMembershipId, setLeadMembershipId] = useState<string>("");

  useEffect(() => {
    if (!open) return;
    setLeadMembershipId(opportunity.producer_id ?? "");
    mutation.reset();
  }, [open, opportunity.producer_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const producerLabel = useMemo(() => {
    const rows = teamMembers.data?.rows ?? [];
    const row = rows.find(
      (r) => r.member_id === opportunity.producer_id,
    );
    if (!row) return "—";
    return row.first_name || row.last_name
      ? `${row.first_name} ${row.last_name}`.trim()
      : row.email;
  }, [teamMembers.data, opportunity.producer_id]);

  const bannerErrors = deriveBannerErrors(
    mutation.isError ? mutation.error : null,
  );

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const input = leadMembershipId
      ? { account_lead_membership_id: leadMembershipId }
      : {};
    mutation.mutate(input, {
      onSuccess: (result) => {
        navigate(`/dashboard/accounts/${result.account_id}`);
        onClose();
      },
    });
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Convert to Account"
      size="md"
      footer={
        <div
          data-testid="convert-modal-footer"
          className="flex items-center justify-end gap-2"
        >
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className={SECONDARY_BUTTON_CLS}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="convert-to-account-form"
            disabled={mutation.isPending}
            data-testid="convert-modal-confirm"
            className={PRIMARY_BUTTON_CLS}
          >
            {mutation.isPending ? "Converting…" : "Confirm conversion"}
          </button>
        </div>
      }
    >
      <form
        id="convert-to-account-form"
        onSubmit={submit}
        data-testid="convert-modal-form"
        className="flex flex-col gap-4"
      >
        {bannerErrors.length > 0 ? (
          <CrmFormBanner errors={bannerErrors} />
        ) : null}

        <section className="flex flex-col gap-2 text-xs">
          <p className="text-text-secondary">
            This will create a new Account from{" "}
            <span className="font-medium text-text-primary">
              {opportunity.name}
            </span>
            , seed the post-conversion playbook activities, and stamp{" "}
            <span className="font-medium text-text-primary">
              {producerLabel}
            </span>{" "}
            as the producer. Producer stays connected for renewals
            and cross-sell regardless of who's servicing day-to-day.
          </p>
        </section>

        <section className="flex flex-col gap-1.5 text-xs">
          <label
            htmlFor="convert-account-lead"
            className="font-medium text-text-secondary"
          >
            Account owner
          </label>
          <select
            id="convert-account-lead"
            data-testid="convert-account-lead-select"
            className={INPUT_CLS}
            value={leadMembershipId}
            onChange={(e) => setLeadMembershipId(e.target.value)}
            disabled={teamMembers.isLoading || mutation.isPending}
          >
            <option value="">(no owner picked yet)</option>
            {(teamMembers.data?.rows ?? []).map((row) => {
              const label = row.first_name || row.last_name
                ? `${row.first_name} ${row.last_name}`.trim()
                : row.email;
              const tail = row.is_producer ? " (producer)" : "";
              return (
                <option key={row.member_id} value={row.member_id}>
                  {label}{tail}
                </option>
              );
            })}
          </select>
          <p className="text-text-tertiary">
            Defaults to the producer. The owner handles the service
            relationship after handoff and can transition
            independently of the producer.
          </p>
        </section>
      </form>
    </Modal>
  );
}


// ── Header control: button or "View Account" link ───────────────────────


export interface ConvertToAccountControlProps {
  opportunity: OpportunityDetail;
}


export function ConvertToAccountControl({
  opportunity,
}: ConvertToAccountControlProps): JSX.Element | null {
  const [open, setOpen] = useState(false);

  if (opportunity.stage !== "closed_won") return null;

  if (opportunity.converted_account_id) {
    return (
      <Link
        to={`/dashboard/accounts/${opportunity.converted_account_id}`}
        data-testid="view-account-link"
        className={[
          "rounded-md border border-border-default bg-raised px-3 py-1.5",
          "text-xs font-medium text-text-primary",
          "hover:bg-raised-hover",
        ].join(" ")}
      >
        View Account →
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        data-testid="convert-to-account-button"
        onClick={() => setOpen(true)}
        className={PRIMARY_BUTTON_CLS}
      >
        Convert to Account
      </button>
      <ConvertToAccountModal
        open={open}
        onClose={() => setOpen(false)}
        opportunity={opportunity}
      />
    </>
  );
}
